import { defineStore } from 'pinia'
import axios from 'axios'

export const ChpPhysiciansStore = defineStore('ChpPhysicians', {
  state: () => ({
    query: {
      searchTerm: '',
      lastNameLetter: '',
      specialty: '',
      subspecialty: '',
      serviceLocation: '',
      zipCode: '',
      language: '',
      gender: '',
      acceptingNewPatients: false,
      sameDayAppointments: false,
      acceptsMediCal: false,
      pageNumber: 1,
      resultsPerPage: 30
    },
    filterOptions: {
      specialties: [],
      subspecialties: [],
      serviceLocations: [],
      languages: [],
      genders: []
    },
    queryStatus: 'loading',
    totalNumberOfResults: 0,
    physicians: [],
    filterOptionsStatus: 'loading',
    urlParametersStatus: 'loading',
    urlParametersDisabled: false,
    siteUrl: ''
  }),
  actions: {
    async getFilterOptions() {
      try {
        this.filterOptionsStatus = 'loading'
        if (window.siteUrl != "") {
          this.siteUrl = window.siteUrl;
        }
        const response = await axios.get(`${this.siteUrl}/webapi/chpphysiciansapi/filteroptions`)
        this.filterOptions = response.data
        this.filterOptionsStatus = 'ready'
        return { success: true }
      }
      catch {
        this.filterOptionsStatus = 'error'
        return { success: false, messages: ['An unexpected error occurred.'] }
      }
    },
    async getResultsFromQuery() {
      try {
        this.queryStatus = 'loading'
        const response = await axios.post(`${this.siteUrl}/webapi/chpphysiciansapi/query`, this.query)
        this.physicians = response.data.physicians
        this.totalNumberOfResults = response.data.totalNumberOfResults
        this.queryStatus = 'ready'
        this.updateUrlParametersFromQuery()
        return { success: true }
      } catch {
        this.queryStatus = 'error'
        return { success: false, messages: ['An unexpected error occurred.'] }
      }
    },
    async updateSearchTerm(searchTerm) {
      this.query.pageNumber = 1
      this.query.searchTerm = searchTerm
      await this.getResultsFromQuery()
    },
    async updateLastNameLetter(lastNameLetter) {
      this.query.pageNumber = 1
      this.query.lastNameLetter = lastNameLetter
      await this.getResultsFromQuery()
    },
    async updateWidgetFilters(searchTerm, specialty, subspecialty, serviceLocation, acceptingNewPatients, sameDayAppointments, acceptsMediCal) {
      this.query.pageNumber = 1
      this.query.searchTerm = searchTerm
      this.query.specialty = specialty
      this.query.subspecialty = subspecialty
      this.query.serviceLocation = serviceLocation
      this.query.acceptingNewPatients = acceptingNewPatients
      this.query.sameDayAppointments = sameDayAppointments
      this.query.acceptsMediCal = acceptsMediCal
      await this.getResultsFromQuery(true)
    },
    async updateFilters(specialty, subspecialty, serviceLocation, zipCode, language, gender, acceptingNewPatients, sameDayAppointments, acceptsMediCal) {
      this.query.pageNumber = 1
      this.query.specialty = specialty
      this.query.subspecialty = subspecialty
      this.query.serviceLocation = serviceLocation
      this.query.zipCode = zipCode
      this.query.language = language
      this.query.gender = gender
      this.query.acceptingNewPatients = acceptingNewPatients
      this.query.sameDayAppointments = sameDayAppointments
      this.query.acceptsMediCal = acceptsMediCal
      await this.getResultsFromQuery()
    },
    async updatePageNumber(pageNumber) {
      this.query.pageNumber = pageNumber
      await this.getResultsFromQuery()
    },
    async disableUrlParameters() {
      this.urlParametersDisabled = true
    },
    async updateQueryFromUrlParameters() {
      this.urlParametersStatus = 'loading'
      if (window.siteUrl != "") {
        this.siteUrl = window.siteUrl;
      }
      const urlParameters = new URLSearchParams(window.location.search)
      if (urlParameters.has('page')) {
        this.query.pageNumber = window.parseInt(urlParameters.get('page'))
      }
      if (urlParameters.has('q')) {
        this.query.searchTerm = urlParameters.get('q')
      }
      if (urlParameters.has('lastnameletter')) {
        this.query.lastNameLetter = urlParameters.get('lastnameletter')
      }
      if (urlParameters.has('specialty')) {
        this.query.specialty = urlParameters.get('specialty')
      }
      if (urlParameters.has('subspecialty')) {
        this.query.subspecialty = urlParameters.get('subspecialty')
      }
      if (urlParameters.has('serviceLocation')) {
        this.query.serviceLocation = urlParameters.get('serviceLocation')
      }
      if (urlParameters.has('zipcode')) {
        this.query.zipCode = urlParameters.get('zipcode')
      }
      if (urlParameters.has('language')) {
        this.query.language = urlParameters.get('language')
      }
      if (urlParameters.has('gender')) {
        this.query.gender = urlParameters.get('gender')
      }
      if (urlParameters.has('acceptingNewPatients')) {
        this.query.acceptingNewPatients = (urlParameters.get('acceptingNewPatients') === 'true')
      }
      if (urlParameters.has('sameDayAppointments')) {
        this.query.sameDayAppointments = (urlParameters.get('sameDayAppointments') === 'true')
      }
      if (urlParameters.has('acceptsMediCal')) {
        this.query.acceptsMediCal = (urlParameters.get('acceptsMediCal') === 'true')
      }
      this.urlParametersStatus = 'ready'
    },
    async updateUrlParametersFromQuery() {
      if (!this.urlParametersDisabled) {
        const urlParameters = new URLSearchParams()
        if (this.query.pageNumber != 1) {
          urlParameters.append('page', this.query.pageNumber)
        }
        if (this.query.searchTerm) {
          urlParameters.append('q', this.query.searchTerm)
        }
        if (this.query.lastNameLetter) {
          urlParameters.append('lastnameletter', this.query.lastNameLetter)
        }
        if (this.query.specialty) {
          urlParameters.append('specialty', this.query.specialty)
        }
        if (this.query.subspecialty) {
          urlParameters.append('subspecialty', this.query.subspecialty)
        }
        if (this.query.serviceLocation) {
          urlParameters.append('serviceLocation', this.query.serviceLocation)
        }
        if (this.query.zipCode) {
          urlParameters.append('zipcode', this.query.zipCode)
        }
        if (this.query.language) {
          urlParameters.append('language', this.query.language)
        }
        if (this.query.gender) {
          urlParameters.append('gender', this.query.gender)
        }
        if (this.query.acceptingNewPatients) {
          urlParameters.append('acceptingNewPatients', this.query.acceptingNewPatients)
        }
        if (this.query.sameDayAppointments) {
          urlParameters.append('sameDayAppointments', this.query.sameDayAppointments)
        }
        if (this.query.acceptsMediCal) {
          urlParameters.append('acceptsMediCal', this.query.acceptsMediCal)
        }
        const newUrl = new URL(window.location)
        newUrl.search = urlParameters.toString()
        window.history.replaceState(null, '', newUrl)
      }
    },
  }
})
